import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { APIError, catchAPIError, ErrorContext } from "@core/dataiku-api/api-error";
import { CardWizardVariable } from "@features/eda/card-models";
import { SampleContextService } from "@features/eda/sample-context.service";
import { CardWizardService } from "@features/eda/worksheet/card-wizard/card-wizard.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { observeFormControl } from "@utils/form-control-observer";
import { toggleFormControls } from "@utils/toggle-form-control";
import { combineLatest, Observable, ReplaySubject } from "rxjs";
import { distinctUntilChanged, filter, map, mergeMap, switchMap } from "rxjs/operators";
import { ACFPlotCard, Card, ListMostFrequentValues, PACF, ResamplerSettings, TimeSeriesCard, UnitRootTestADF, UnitRootTestADFCard, UnitRootTestKPSS, UnitRootTestKPSSCard, UnitRootTestZA, UnitRootTestZACard } from "src/generated-sources";

// For quickly accessing the types and guards.
const {
    InterpolationMethod,
    ExtrapolationMethod,
    TimeUnit,
    DayOfWeek,
    DuplicateTimestampsHandlingMethod,
} = ResamplerSettings;

const {
    isACFPlotCard,
    isUnitRootTestADFCard,
    isUnitRootTestCard,
    isUnitRootTestKPSSCard,
    isUnitRootTestZACard,
} = Card;

interface SeriesIdValuesCache {
    [column: string]: string[];
}

@UntilDestroy()
@Component({
    selector: 'time-series-card-config',
    templateUrl: './time-series-card-config.component.html',
    styleUrls: [
        './time-series-card-config.component.less',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeSeriesCardConfigComponent implements OnChanges, OnDestroy, ErrorContext {
    @Input() params: TimeSeriesCard;
    params$ = new ReplaySubject<TimeSeriesCard>(1);
    @Output() paramsChange = new EventEmitter<TimeSeriesCard>(true);
    @Output() validityChange = new EventEmitter<boolean>(true);

    configForm: FormGroup;
    private resamplerSettingsForm: FormGroup;
    private identifierValuesForm: FormGroup;

    private allVariables$: Observable<CardWizardVariable[]>;
    seriesVariables$: Observable<CardWizardVariable[]>;
    timeVariables$: Observable<CardWizardVariable[]>;
    identifierVariables$: Observable<CardWizardVariable[]>;

    private suggestedValuesCache: SeriesIdValuesCache = {};

    error?: APIError | null;

    constructor(
        private fb: FormBuilder,
        private cardWizardService: CardWizardService,
        private changeDetectorRef: ChangeDetectorRef,
        private sampleContextService: SampleContextService,
    ) {
        // Set default values for the resampling
        this.resamplerSettingsForm = this.fb.group({
            nUnits: this.fb.control(1, [Validators.required, Validators.min(1)]),
            timeUnit: this.fb.control(TimeUnit.DAY, Validators.required),
            timeUnitEndOfWeek: this.fb.control(DayOfWeek.SUNDAY, Validators.required),
            interpolationMethod: this.fb.control(InterpolationMethod.LINEAR, Validators.required),
            interpolationConstantValue: this.fb.control(null, Validators.required),
            extrapolationMethod: this.fb.control(ExtrapolationMethod.NO_EXTRAPOLATION, Validators.required),
            extrapolationConstantValue: this.fb.control(null, Validators.required),
            duplicateTimestampsHandlingMethod: this.fb.control(DuplicateTimestampsHandlingMethod.FAIL_IF_CONFLICTING, Validators.required),
        });

        // Init identifier form
        this.identifierValuesForm = this.fb.group({});

        this.configForm = this.fb.group({
            // Fields that are common to every time series card
            seriesColumn: this.fb.control(null, Validators.required),
            timeColumn: this.fb.control(null, Validators.required),

            // Long format fields
            useLongFormat: this.fb.control(null, Validators.required),
            identifierColumns: this.fb.control([], [Validators.required, Validators.minLength(1)]),
            identifierValues: this.identifierValuesForm,

            // Type of the card to configure (not editable in the UI)
            type: this.fb.control(null, Validators.required),

            // Number of lags (common setting to unit root tests and acf/pacf)
            autoComputeLags: this.fb.control(null, Validators.required),
            nLags: this.fb.control(null, [Validators.required, Validators.min(1)]),

            // Resampler settings
            useResampling: this.fb.control(null, Validators.required),
            resamplerSettings: this.resamplerSettingsForm,

            adfOptions: this.fb.group({
                regressionMode: this.fb.control(null, Validators.required),
            }),

            zaOptions: this.fb.group({
                regressionMode: this.fb.control(null, Validators.required),
            }),

            kpssOptions: this.fb.group({
                regressionMode: this.fb.control(null, Validators.required),
            }),

            acfPlotOptions: this.fb.group({
                showSummary: this.fb.control(null, Validators.required),
                isPartial: this.fb.control(null, Validators.required),
                adjusted: this.fb.control(null, Validators.required),
                pacfMethod: this.fb.control(null, Validators.required),
            }),
        });

        toggleFormControls([
            {
                control: this.configForm.controls.adfOptions,
                condition: this.configForm.controls.type,
                value: UnitRootTestADFCard.type
            },
            {
                control: this.configForm.controls.zaOptions,
                condition: this.configForm.controls.type,
                value: UnitRootTestZACard.type
            },
            {
                control: this.configForm.controls.kpssOptions,
                condition: this.configForm.controls.type,
                value: UnitRootTestKPSSCard.type
            },
            {
                control: this.configForm.controls.acfPlotOptions,
                condition: this.configForm.controls.type,
                value: ACFPlotCard.type
            },
            {
                control: this.configForm.controls.identifierColumns,
                condition: this.configForm.controls.useLongFormat,
                value: true
            },
            {
                control: this.configForm.controls.autoComputeLags,
                condition: this.configForm.controls.type,
                value: (type: Card["type"]) =>
                    type === ACFPlotCard.type ||
                    type === UnitRootTestADFCard.type ||
                    type === UnitRootTestZACard.type ||
                    type === UnitRootTestKPSSCard.type
            },
            {
                control: this.configForm.controls.nLags,
                condition: this.configForm.controls.autoComputeLags,
                value: false
            },
            {
                control: this.resamplerSettingsForm,
                condition: this.configForm.controls.useResampling,
                value: true,
                children: [
                    {
                        control: this.resamplerSettingsForm.controls.timeUnitEndOfWeek,
                        condition: this.resamplerSettingsForm.controls.timeUnit,
                        value: TimeUnit.WEEK
                    },
                    {
                        control: this.resamplerSettingsForm.controls.interpolationConstantValue,
                        condition: this.resamplerSettingsForm.controls.interpolationMethod,
                        value: InterpolationMethod.CONSTANT
                    },
                    {
                        control: this.resamplerSettingsForm.controls.extrapolationConstantValue,
                        condition: this.resamplerSettingsForm.controls.extrapolationMethod,
                        value: ExtrapolationMethod.CONSTANT
                    }
                ]
            }
        ]);

        this.configForm.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(formValue => {
                const resamplerSettings = formValue.useResampling ?
                    formValue.resamplerSettings : null;

                const identifierColumns: CardWizardVariable[] = formValue.identifierColumns ?? [];
                const identifierValues: { [key:string]: string[] } = formValue.identifierValues ?? {};
                const seriesIdentifiers: TimeSeriesCard.TimeSeriesIdentifier[] = identifierColumns.map(column => {
                    return {
                        column,
                        values: identifierValues[column.name] ?? [],
                    };
                });

                let newCard: TimeSeriesCard = {
                    ...this.params,
                    seriesColumn: formValue.seriesColumn,
                    timeColumn: formValue.timeColumn,
                    seriesIdentifiers,
                    resamplerSettings,
                };

                const nLags = formValue.nLags ?? null;

                if (isUnitRootTestADFCard(newCard)) {
                    newCard = {
                        ...newCard,
                        ...formValue.adfOptions,
                        nLags,
                    };
                } else if (isUnitRootTestZACard(newCard)) {
                    newCard = {
                        ...newCard,
                        ...formValue.zaOptions,
                        nLags,
                    };
                } else if (isUnitRootTestKPSSCard(newCard)) {
                    newCard = {
                        ...newCard,
                        ...formValue.kpssOptions,
                        nLags,
                    };
                } else if (isACFPlotCard(newCard)) {
                    newCard = {
                        ...newCard,
                        ...formValue.acfPlotOptions,
                        nLags,
                    };
                }

                this.paramsChange.emit(newCard);
            });

        this.configForm.statusChanges
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.validityChange.emit(this.configForm.valid);
            });

        const cardType$ = this.params$.pipe(
            map(params => params.type),
            distinctUntilChanged(),
        );

        this.allVariables$ = cardType$.pipe(
            switchMap(type => this.cardWizardService.availableVariables(type))
        );

        this.seriesVariables$ = cardType$.pipe(
            switchMap(type => this.cardWizardService.availableVariables(type, { isSeriesVariable: true }))
        );

        this.timeVariables$ = cardType$.pipe(
            switchMap(type => this.cardWizardService.availableVariables(type, { isTimeVariable: true }))
        );

        // We disable the selected series / time column to discourage the user
        // from selecting those again as identifiers.
        this.identifierVariables$ = combineLatest([observeFormControl(this.configForm), this.allVariables$]).pipe(
            map(([formValue, allVariables]) => {
                const { seriesColumn, timeColumn } = formValue;
                return allVariables.map(v => {
                    const disabled = v.name === seriesColumn?.name
                        || v.name === timeColumn?.name;

                    return {
                        ...v,
                        disabled,
                    };
                });
            }),
        );

        // Maintain the form consistent with newly added series identifiers.
        observeFormControl<CardWizardVariable[]>(this.configForm.controls.identifierColumns)
            .pipe(
                untilDestroyed(this),
                switchMap(columns => columns.map(column => column.name)),
                filter(column => !this.identifierValuesForm.contains(column))
            )
            .subscribe(column => {
                this.identifierValuesForm.addControl(column, this.fb.control([]));
            });

        // Cache the values suggestions for each new series identifier.
        observeFormControl<CardWizardVariable[]>(this.configForm.controls.identifierColumns)
            .pipe(
                untilDestroyed(this),
                switchMap(columns => columns.map(column => column.name)),
                filter(column => !(column in this.suggestedValuesCache)),
                mergeMap(column =>
                    this.sampleContextService.runInteractiveQuery({
                        type: ListMostFrequentValues.type,
                        column,
                        maxValues: 100,
                    }).pipe(
                        catchAPIError(this),
                        map(res => ({ column, values: res.values ?? [] }))
                    )
                )
            ).subscribe(({ column, values }) => {
                this.suggestedValuesCache = {
                    ...this.suggestedValuesCache,
                    [column]: values,
                };

                this.changeDetectorRef.markForCheck();
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.params == null) {
            return;
        }

        this.params$.next(this.params);
        const card = this.params;
        const seriesIdentifiers = card.seriesIdentifiers ?? [];
        const useLongFormat = seriesIdentifiers.length > 0;
        const useResampling = card.resamplerSettings != null;
        const identifierColumns = seriesIdentifiers.map(id => id.column);

        let identifierValues = {};
        seriesIdentifiers.forEach(seriesId => {
            identifierValues = {
                ...identifierValues,
                [seriesId.column.name]: seriesId.values,
            };
        });

        this.configForm.patchValue({
            type: card.type,
            seriesColumn: card.seriesColumn,
            timeColumn: card.timeColumn,
            useLongFormat,
            identifierColumns,
            identifierValues,
            useResampling,
            resamplerSettings: card.resamplerSettings,
        });

        if (isUnitRootTestADFCard(card)) {
            this.configForm.controls.adfOptions.patchValue({
                regressionMode: card.regressionMode,
            });
        } else if (isUnitRootTestZACard(card)) {
            this.configForm.controls.zaOptions.patchValue({
                regressionMode: card.regressionMode,
            });
        } else if (isUnitRootTestKPSSCard(card)) {
            this.configForm.controls.kpssOptions.patchValue({
                regressionMode: card.regressionMode,
            });
        } else if (isACFPlotCard(card)) {
            this.configForm.controls.acfPlotOptions.patchValue({
                showSummary: card.showSummary,
                isPartial: card.isPartial,
                adjusted: card.adjusted,
                pacfMethod: card.pacfMethod,
            });
        }

        if (isACFPlotCard(card) || isUnitRootTestCard(card)) {
            this.configForm.patchValue({
                autoComputeLags: card.nLags == null,
                nLags: card.nLags,
            });
        } else {
            this.configForm.patchValue({
                autoComputeLags: null,
                nLags: null,
            });
        }
    }

    ngOnDestroy(): void {
        // required by @UntilDestroy
    }

    get selectedIdentifiers(): CardWizardVariable[] {
        return (this.configForm.controls.identifierColumns as FormControl).value;
    }

    getIdentifierValuesControl(identifier: CardWizardVariable): FormControl {
        return this.identifierValuesForm.get(identifier.name) as FormControl;
    }

    hasNoSelectedValues(identifier: CardWizardVariable): boolean {
        return this.getIdentifierValuesControl(identifier).value.length === 0;
    }

    useAllValues(identifier: CardWizardVariable) {
        this.getIdentifierValuesControl(identifier).setValue([]);
    }

    isLoadingSuggestions(identifier: CardWizardVariable): boolean {
        return !(identifier.name in this.suggestedValuesCache);
    }

    getSuggestions(identifier: CardWizardVariable): string[] {
        return this.suggestedValuesCache[identifier.name];
    }

    adfRegressionOptions = [
        {
            name: 'Constant only',
            value: UnitRootTestADF.RegressionMode.CONSTANT_ONLY,
        },
        {
            name: 'Constant, linear trend',
            value: UnitRootTestADF.RegressionMode.CONSTANT_WITH_LINEAR_TREND,
        },
        {
            name: 'Constant, linear and quadratic trend',
            value: UnitRootTestADF.RegressionMode.CONSTANT_WITH_LINEAR_QUADRATIC_TREND,
        },
        {
            name: 'No constant, no trend',
            value: UnitRootTestADF.RegressionMode.NO_CONSTANT_NO_TREND,
        },
    ];

    zaRegressionOptions = [
        {
            name: 'Constant only',
            value: UnitRootTestZA.RegressionMode.CONSTANT_ONLY,
        },
        {
            name: 'Trend only',
            value: UnitRootTestZA.RegressionMode.TREND_ONLY,
        },
        {
            name: 'Constant with trend',
            value: UnitRootTestZA.RegressionMode.CONSTANT_WITH_TREND,
        },
    ];

    kpssRegressionOptions = [
        {
            name: 'Constant only',
            value: UnitRootTestKPSS.RegressionMode.CONSTANT,
        },
        {
            name: 'Constant with trend',
            value: UnitRootTestKPSS.RegressionMode.CONSTANT_WITH_TREND,
        },
    ];

    get isPartialACF(): boolean {
        const acfPlotOptions = this.configForm.controls.acfPlotOptions as FormGroup;
        return acfPlotOptions.controls.isPartial.value === true;
    }

    pacfMethodOptions = [
        {
            name: 'Yule-Walker',
            value: PACF.Method.YULE_WALKER,
        },
        {
            name: 'Regression on lags and on constant',
            value: PACF.Method.OLS,
        },
        {
            name: 'Regression on lags using bias adjustment',
            value: PACF.Method.OLS_UNBIASED,
        },
        {
            name: 'Levinson-Durbin recursion',
            value: PACF.Method.LEVINSON_DURBIN,
        },
    ];

    get useLongFormat(): boolean {
        return this.configForm.controls.useLongFormat.value === true;
    }

    get isAutoComputeLagsEnabled(): boolean {
        return this.configForm.controls.autoComputeLags.enabled;
    }

    get autoComputeLags(): boolean {
        return this.configForm.controls.autoComputeLags.value === true;
    }

    get useResampling(): boolean {
        return this.configForm.controls.useResampling.value === true;
    }

    get hasConstantInterpolation(): boolean {
        return this.resamplerSettingsForm.controls.interpolationMethod.value === InterpolationMethod.CONSTANT;
    }

    get hasConstantExtrapolation(): boolean {
        return this.resamplerSettingsForm.controls.extrapolationMethod.value === ExtrapolationMethod.CONSTANT;
    }

    get hasWeeklyResampling(): boolean {
        return this.resamplerSettingsForm.controls.timeUnit.value === TimeUnit.WEEK;
    }

    interpolationOptions = [
        { name: 'Nearest value', value: InterpolationMethod.NEAREST },
        { name: 'Previous value', value: InterpolationMethod.PREVIOUS },
        { name: 'Next value', value: InterpolationMethod.NEXT },
        { name: 'Linear', value: InterpolationMethod.LINEAR },
        { name: 'Quadratic', value: InterpolationMethod.QUADRATIC },
        { name: 'Cubic', value: InterpolationMethod.CUBIC },
        { name: 'Constant value', value: InterpolationMethod.CONSTANT },
    ];

    extrapolationOptions = [
        { name: 'No extrapolation', value: ExtrapolationMethod.NO_EXTRAPOLATION },
        { name: 'Use previous or next value', value: ExtrapolationMethod.PREVIOUS_NEXT },
        { name: 'Constant value', value: ExtrapolationMethod.CONSTANT },
        { name: 'Linear', value: ExtrapolationMethod.LINEAR },
        { name: 'Quadratic', value: ExtrapolationMethod.QUADRATIC },
        { name: 'Cubic', value: ExtrapolationMethod.CUBIC },
    ];

    timeUnitOptions = [
        { name: 'Millisecond', value: TimeUnit.MILLISECOND },
        { name: 'Second', value: TimeUnit.SECOND },
        { name: 'Minute', value: TimeUnit.MINUTE },
        { name: 'Hour', value: TimeUnit.HOUR },
        { name: 'Business day', value: TimeUnit.BUSINESS_DAY },
        { name: 'Day', value: TimeUnit.DAY },
        { name: 'Week', value: TimeUnit.WEEK },
        { name: 'Month', value: TimeUnit.MONTH },
        { name: 'Quarter', value: TimeUnit.QUARTER },
        { name: 'Half year', value: TimeUnit.HALF_YEAR },
        { name: 'Year', value: TimeUnit.YEAR },
    ];

    dayOfWeekOptions = [
        { name: 'Monday', value: DayOfWeek.MONDAY },
        { name: 'Tuesday', value: DayOfWeek.TUESDAY },
        { name: 'Wednesday', value: DayOfWeek.WEDNESDAY },
        { name: 'Thursday', value: DayOfWeek.THURSDAY },
        { name: 'Friday', value: DayOfWeek.FRIDAY },
        { name: 'Saturday', value: DayOfWeek.SATURDAY },
        { name: 'Sunday', value: DayOfWeek.SUNDAY },
    ];

    duplicateTimestampsHandlingOptions = [
        { name: 'Fail on conflicting duplicates', value: DuplicateTimestampsHandlingMethod.FAIL_IF_CONFLICTING },
        { name: 'Drop all conflicting duplicates', value: DuplicateTimestampsHandlingMethod.DROP_IF_CONFLICTING },
        { name: 'Use mean of numerical variables', value: DuplicateTimestampsHandlingMethod.MEAN_MODE },
    ];

    pushError(error: APIError | null) {
        this.error = error;
        this.changeDetectorRef.markForCheck();
    }

    get hasSettings() {
        return isACFPlotCard(this.params)
            || isUnitRootTestADFCard(this.params)
            || isUnitRootTestKPSSCard(this.params)
            || isUnitRootTestZACard(this.params)
    }
}
